import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { Flag, Loader2 } from "lucide-react";

import { getErrorMessage } from "@/lib/errorMessage";

// Slice 1F: post-login landing is /day for every non-coach title.
// Shop titles still bounce to /fleet inside Protected.
const _landingFor = (_userObj) => "/day";

/**
 * Login page.
 *
 * Feb 2026: Google SSO removed. Self-service `/auth/register` removed.
 * New users are invited via the Access page — an admin generates a
 * temp password, hands it to the invitee out-of-band, and the invitee
 * completes ForcePasswordChange on first login. Recovery = "Contact
 * your admin to reset it." (static copy under the submit button).
 */
export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loginWithToken } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  // MFA two-step state — populated only when the /auth/login response
  // sets `requires_mfa: true`. Cleared on successful verify or when
  // the user backs out to the password screen. The pending token
  // expires server-side in ~3 min; we mirror the deadline locally
  // so the "Session expired" hint kicks in without waiting for the
  // verify call to 401.
  const [mfa, setMfa] = useState(null); // { pending_mfa_token, expires_at }
  const [mfaCode, setMfaCode] = useState("");

  useEffect(() => { if (user) navigate(_landingFor(user), { replace: true }); }, [user, navigate]);

  useEffect(() => {
    if (location.state?.authError) {
      toast.error(location.state.authError);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post("/auth/login", { email, password });
      if (res.data?.requires_mfa) {
        // Password verified; second factor required.
        setMfa({
          pending_mfa_token: res.data.pending_mfa_token,
          expires_at: Date.now() + (res.data.expires_in || 180) * 1000,
        });
        setMfaCode("");
        return;
      }
      loginWithToken(res.data.access_token, res.data.user);
      toast.success(`Welcome, ${res.data.user.name || res.data.user.email}`);
      navigate(_landingFor(res.data.user), { replace: true });
    } catch (err) {
      toast.error(getErrorMessage(err, "Authentication failed"));
    } finally {
      setBusy(false);
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    if (!mfa) return;
    setBusy(true);
    try {
      const res = await api.post("/auth/mfa/verify", {
        pending_mfa_token: mfa.pending_mfa_token,
        code: mfaCode,
      });
      loginWithToken(res.data.access_token, res.data.user);
      toast.success(`Welcome, ${res.data.user.name || res.data.user.email}`);
      navigate(_landingFor(res.data.user), { replace: true });
    } catch (err) {
      const msg = getErrorMessage(err, "MFA verification failed");
      toast.error(msg);
      // If the server ended the pending session, drop back to
      // password entry so the user isn't stuck on a dead form.
      if (/expired|sign in again/i.test(msg)) {
        setMfa(null);
        setMfaCode("");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="relative hidden lg:block overflow-hidden">
        <img
          src="https://images.pexels.com/photos/36626909/pexels-photo-36626909.jpeg"
          alt="Porsche on track"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/10" />
        <div className="absolute bottom-0 left-0 p-12">
          <div className="label-tag mb-3 text-white/70">Porsche Experience Center · Atlanta</div>
          <h1 className="font-head text-5xl font-extrabold tracking-tighter text-white leading-none">
            Drive<br />Operations<br /><span className="text-[#ff3b52]">Command.</span>
          </h1>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 grain">
        <div className="w-full max-w-sm animate-in fade-in-0 slide-in-from-bottom-4 duration-500">
          <div className="flex items-center gap-2 mb-8">
            <div className="h-9 w-9 grid place-items-center bg-[#D5001C] rounded-sm">
              <Flag className="h-5 w-5 text-white" />
            </div>
            <span className="font-head font-bold tracking-tight text-lg">PEC ATL OPS</span>
          </div>

          <h2 className="font-head text-2xl font-bold tracking-tight mb-1">
            {mfa ? "Two-factor code" : "Sign in"}
          </h2>
          <p className="text-sm text-zinc-500 mb-6">
            {mfa
              ? "Enter the 6-digit code from your authenticator app, or one of your backup codes."
              : "Manager & operations access only."}
          </p>

          {!mfa && (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="label-tag" htmlFor="email">Email</Label>
                <Input id="email" data-testid="email-input" type="email" value={email}
                  onChange={(e) => setEmail(e.target.value)} required
                  className="mt-1.5 bg-secondary border-zinc-800 rounded-sm" />
              </div>
              <div>
                <Label className="label-tag" htmlFor="password">Password</Label>
                <PasswordInput id="password" testId="password-input" value={password}
                  onChange={(e) => setPassword(e.target.value)} required
                  className="mt-1.5 bg-secondary border-zinc-800 rounded-sm" />
              </div>
              <Button type="submit" data-testid="submit-auth-btn" disabled={busy}
                className="w-full bg-[#D5001C] hover:bg-[#B90018] rounded-sm font-semibold">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
              </Button>
              <p data-testid="contact-admin-copy"
                className="text-xs text-zinc-500 text-center pt-2">
                <span className="text-zinc-400">Forgot your password?</span>{" "}
                Contact your admin to reset it.
              </p>
            </form>
          )}

          {mfa && (
            <form onSubmit={submitMfa} className="space-y-4" data-testid="mfa-verify-form">
              <div>
                <Label className="label-tag" htmlFor="mfa-code">Code</Label>
                <Input id="mfa-code" data-testid="mfa-code-input"
                  type="text" inputMode="text" autoComplete="one-time-code"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  placeholder="123456 or XXXX-XXXX"
                  required autoFocus
                  className="mt-1.5 bg-secondary border-zinc-800 rounded-sm tracking-widest" />
              </div>
              <Button type="submit" data-testid="mfa-verify-submit-btn" disabled={busy || !mfaCode}
                className="w-full bg-[#D5001C] hover:bg-[#B90018] rounded-sm font-semibold">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify"}
              </Button>
              <button type="button"
                data-testid="mfa-back-to-password-btn"
                onClick={() => { setMfa(null); setMfaCode(""); }}
                className="w-full text-xs text-zinc-500 hover:text-zinc-300 text-center pt-2">
                ← Back to password
              </button>
            </form>
          )}

          <div
            data-testid="login-vendor-footer"
            className="mt-10 pt-4 border-t border-zinc-900 text-[10px] tracking-wider uppercase text-zinc-600 text-center">
            Powered by Gridline Systems LLC
          </div>
        </div>
      </div>
    </div>
  );
}
